import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Loader2, Check, AlertCircle, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CityCard = ({ city, onReplaced }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Cache-bust by appending a timestamp on successful upload so the new image
  // shows immediately even though Cloudinary CDN already invalidated.
  const [cacheBust, setCacheBust] = useState(0);

  const uploadFile = async (file) => {
    if (!file) return;
    setError('');
    setSuccess(false);

    if (!file.type.startsWith('image/')) {
      setError('Must be an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(`Too large (${(file.size / 1024 / 1024).toFixed(1)} MB) - max 10 MB`);
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch(`${API_URL}/api/admin/cities/${city.slug}/hero`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Upload failed');
      }
      setSuccess(true);
      setCacheBust(Date.now());
      onReplaced(city.slug, data);
      setTimeout(() => setSuccess(false), 4000);
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadFile(f);
  };

  const displayUrl = cacheBust
    ? `${city.hero_image_url}?v=${cacheBust}`
    : city.hero_image_url;

  return (
    <div
      data-testid={`admin-city-card-${city.slug}`}
      className="bg-white border border-[#0A1F2E]/10 rounded-lg overflow-hidden hover:border-[#2CA8A0]/40 transition"
    >
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative aspect-[4/3] cursor-pointer bg-[#F4ECD8] group ${
          dragOver ? 'ring-4 ring-[#2CA8A0]' : ''
        }`}
      >
        <img
          src={displayUrl}
          alt={`${city.name} hero`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A1F2E]/0 group-hover:bg-[#0A1F2E]/60 transition flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition text-white text-center px-4">
            <Upload className="w-7 h-7 mx-auto mb-2" />
            <p className="font-mono uppercase text-[11px] tracking-[0.15em]">
              Drop or click to replace
            </p>
            <p className="font-body text-xs mt-1 opacity-80">JPG / PNG · max 10 MB</p>
          </div>
        </div>

        {uploading && (
          <div className="absolute inset-0 bg-[#0A1F2E]/80 flex items-center justify-center">
            <div className="text-white text-center">
              <Loader2 className="w-7 h-7 mx-auto mb-2 animate-spin" />
              <p className="font-mono uppercase text-[11px] tracking-[0.15em]">
                Uploading to Cloudinary…
              </p>
            </div>
          </div>
        )}

        {success && (
          <div className="absolute top-3 right-3 bg-[#2CA8A0] text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
            <Check className="w-3.5 h-3.5" />
            <span className="font-mono uppercase text-[10px] tracking-[0.12em] font-semibold">
              Replaced
            </span>
          </div>
        )}

        {city.managed_by === 'admin' && (
          <div
            className="absolute top-3 left-3 bg-[#E8A830] text-[#0A1F2E] px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm"
            data-testid={`admin-city-managed-${city.slug}`}
            title="Admin-curated - survives cold-start reseeds"
          >
            <ShieldCheck className="w-3 h-3" />
            <span className="font-mono uppercase text-[9px] tracking-[0.12em] font-bold">
              Admin
            </span>
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-display italic text-[#0A1F2E] text-xl leading-tight">
          {city.name}
        </h3>
        <p className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#0A1F2E]/55 mt-1">
          {city.state} · /{city.slug}
        </p>
        {city.tagline && (
          <p className="font-body text-sm text-[#0A1F2E]/70 mt-2 line-clamp-2">
            {city.tagline}
          </p>
        )}

        {error && (
          <div
            className="mt-3 flex items-start gap-2 text-[#B91C1C] bg-[#FEE2E2] px-3 py-2 rounded text-sm"
            data-testid={`admin-city-error-${city.slug}`}
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`admin-city-file-${city.slug}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) uploadFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
};

const AdminCityPhotos = () => {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const v = await fetch(`${API_URL}/api/admin/verify`, { credentials: 'include' });
        if (!v.ok) {
          navigate('/admin');
          return;
        }
        const r = await fetch(`${API_URL}/api/admin/cities`, { credentials: 'include' });
        if (!r.ok) throw new Error('Failed to load cities');
        const d = await r.json();
        setCities(d.cities || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const onReplaced = (slug, data) => {
    setCities((prev) =>
      prev.map((c) =>
        c.slug === slug
          ? { ...c, hero_image_url: data.hero_image_url, managed_by: 'admin', updated_at: data.updated_at }
          : c
      )
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]" data-testid="admin-cities-page">
      <header className="border-b border-[#0A1F2E]/10 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="font-mono uppercase text-[11px] tracking-[0.15em] text-[#0A1F2E]/65 hover:text-[#0A1F2E] flex items-center gap-1.5"
              data-testid="admin-cities-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
            </Link>
            <span className="text-[#0A1F2E]/25">·</span>
            <h1 className="font-display italic text-2xl md:text-3xl">City Photos</h1>
          </div>
          <span
            className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 hidden md:block"
            data-testid="admin-cities-count"
          >
            {cities.length} cities · Cloudinary
          </span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-8 max-w-3xl">
          <p className="font-body text-[#0A1F2E]/75 leading-relaxed">
            Drag &amp; drop or click any card to replace its hero photo. Uploads go straight to your
            Cloudinary library (<code className="font-mono text-[13px] bg-[#F4ECD8] px-1.5 py-0.5 rounded">mundalo/cities/&lt;slug&gt;</code>),
            overwrite the existing asset, and bust the CDN cache instantly. Cards with the
            <span className="inline-flex items-center gap-1 mx-1.5 bg-[#E8A830] text-[#0A1F2E] px-1.5 py-0.5 rounded text-[10px] font-mono font-bold uppercase">
              <ShieldCheck className="w-2.5 h-2.5 inline" /> Admin
            </span>
            badge are protected from cold-start reseeds.
          </p>
        </div>

        {loading && (
          <p className="font-mono uppercase text-[11px] tracking-[0.15em] text-[#0A1F2E]/55">
            Loading cities…
          </p>
        )}

        {error && (
          <div className="bg-[#FEE2E2] text-[#B91C1C] p-4 rounded mb-6" data-testid="admin-cities-error">
            {error}
          </div>
        )}

        {!loading && cities.length > 0 && (
          <div
            data-testid="admin-cities-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {cities.map((c) => (
              <CityCard key={c.slug} city={c} onReplaced={onReplaced} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminCityPhotos;
